// ---------------------------------------------------------------------------
// V11Layout — the application shell
// ---------------------------------------------------------------------------
// Sidebar (expanded or rail), topbar, and the content area. Pages inject
// their own controls into the topbar through V11LayoutProvider —
// `useV11Layout().setHeaderControls` — which is how the time bar gets there.
//
// AL-1850 moved the sidebar's parts into src/components/v11/shell; what is
// left here is the arrangement and the two pieces of state the arrangement
// needs: which items the account may see, and where a nav link should point.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { V11LayoutProvider, useV11Layout } from '@/contexts/V11LayoutContext';
import { useProfile } from '@/hooks/useProfile';
import { fromBackendLanguage } from '@/i18n/languageCodes';
import { ShellSidebar } from '@/components/v11/shell/ShellSidebar';
import { ShellTopbar } from '@/components/v11/shell/ShellTopbar';
import { MobileNavDrawer } from '@/components/v11/shell/MobileNavDrawer';
import { useMinWidth } from '@/components/v11/shell/useMinWidth';
import { usePersistedFlag } from '@/components/v11/shell/usePersistedFlag';
import {
  RAIL_WIDTH_PX,
  SIDEBAR_PINNED_KEY,
  SIDEBAR_WIDTH_PX,
  SIDEBAR_DRAWER_MAX_WIDTH_PX,
  SIDEBAR_FULL_MIN_WIDTH_PX,
} from '@/components/v11/shell/constants';
import { FELTON_NAV_ITEMS, visibleNavItems } from '@/components/v11/shell/navItems';

interface V11LayoutInnerProps {
  readonly children?: React.ReactNode;
}

const V11LayoutInner: React.FC<V11LayoutInnerProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const { headerControls } = useV11Layout();
  const { data: profile } = useProfile();
  const [pinned, , togglePinned] = usePersistedFlag(SIDEBAR_PINNED_KEY, true);

  // Scope the primary colour to the accent while the app is on screen (AL-1904).
  // On <html> rather than on the layout: dialogs and popovers render in a portal.
  // A layout effect, so the first paint already has it.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add('v11-app');
    return () => root.classList.remove('v11-app');
  }, []);

  // Apply the language saved in the user profile (backend) once it loads.
  // Without this, the preference chosen in /v11/profile only lasted until the
  // next full page load.
  // The profile carries the backend's code (`br`); i18next takes BCP 47 (AL-1843).
  useEffect(() => {
    const saved = fromBackendLanguage(profile?.language);
    if (saved && i18n.language !== saved) {
      i18n.changeLanguage(saved);
    }
  }, [profile?.language, i18n]);

  const navItems = useMemo(
    () => visibleNavItems(FELTON_NAV_ITEMS, profile?.settings, import.meta.env),
    [profile?.settings]
  );

  // Preserve Discovery's search params when navigating away and back.
  const discoverySearchRef = useRef<string>('');
  const isOnDiscovery = location.pathname === '/v11/discovery';
  // AL-1854: the Story is full-bleed in Figma, with no shell top bar above it.
  const isOnStory = location.pathname.startsWith('/v11/story/');

  useEffect(() => {
    if (isOnDiscovery) {
      discoverySearchRef.current = location.search;
    }
  }, [isOnDiscovery, location.search]);

  const getNavTo = useCallback(
    (path: string): string => {
      if (path === '/v11/discovery' && !isOnDiscovery && discoverySearchRef.current) {
        return `${path}${discoverySearchRef.current}`;
      }

      // Carry `ioc=0` (explicitly OFF) between Channels and Discovery. Both
      // default to ON when absent, so only the OFF state needs propagating.
      const isCrossNav = path === '/v11/channels' || path === '/v11/discovery';
      if (isCrossNav) {
        const currentIoc = new URLSearchParams(location.search).get('ioc');
        if (currentIoc === '0') {
          const sep = path.includes('?') ? '&' : '?';
          return `${path}${sep}ioc=0`;
        }
      }

      // "Compare" from inside Compare: the same URL without the open tab, so a
      // detail view goes back to the overview and the rest (`ioc`) stays. A
      // plain `/v11/compare` would keep the tab, being the same route (AL-1951).
      if (path === '/v11/compare' && location.pathname === path) {
        const params = new URLSearchParams(location.search);
        params.delete('view');
        const query = params.toString();
        return query ? `${path}?${query}` : path;
      }

      return path;
    },
    [isOnDiscovery, location.pathname, location.search]
  );

  // Below `lg` the sidebar is always the rail (AL-1911): at ~970px a 280px
  // sidebar would leave the pages cramped. There is no row of cards on top at
  // any width any more — it cut its items off and is not in the Figma, which
  // only draws the sidebar, open or collapsed.
  const fullSidebarFits = useMinWidth(SIDEBAR_FULL_MIN_WIDTH_PX);
  const collapsed = !pinned || !fullSidebarFits;

  // AL-1972 · below sm there is no rail: the sidebar opens in a drawer from the
  // topbar, and closes again once a page is picked.
  const railFits = useMinWidth(SIDEBAR_DRAWER_MAX_WIDTH_PX);
  const [navOpen, setNavOpen] = useState(false);
  // Every navigation closes it, a link to the page already open included:
  // `key` changes on each one even when the path and the query do not.
  useEffect(() => {
    setNavOpen(false);
  }, [location.key, location.pathname, location.search]);
  // Rotated past sm with the drawer open, the rail takes over; closing it here
  // stops the drawer reopening by itself on the way back.
  useEffect(() => {
    if (railFits) setNavOpen(false);
  }, [railFits]);
  const sidebarWidth = !railFits ? 0 : collapsed ? RAIL_WIDTH_PX : SIDEBAR_WIDTH_PX;

  return (
    <div className="flex min-h-screen overflow-x-hidden bg-v11-bg text-v11-text-primary">
      {railFits ? (
        <ShellSidebar
          items={navItems}
          pathname={location.pathname}
          collapsed={collapsed}
          onToggleCollapsed={togglePinned}
          getNavTo={getNavTo}
          canToggle={fullSidebarFits}
        />
      ) : (
        <MobileNavDrawer
          open={navOpen}
          onOpenChange={setNavOpen}
          sidebar={{ items: navItems, pathname: location.pathname, getNavTo }}
        />
      )}

      {/* The content starts where the sidebar ends. The width travels as a
          custom property so it is only written down once, in constants.ts. */}
      <div
        className="flex flex-1 flex-col overflow-x-hidden transition-[margin] duration-200 ease-in-out ml-(--shell-sidebar-width)"
        style={
          {
            '--shell-sidebar-width': `${sidebarWidth}px`,
          } as React.CSSProperties
        }
      >
        {!isOnStory && (
          <ShellTopbar
            pathname={location.pathname}
            headerControls={headerControls}
            onOpenNav={railFits ? undefined : () => setNavOpen(true)}
          />
        )}

        {/* Narrower margins on narrow screens (AL-1911): beside the 72px rail from
            sm, the whole width below it (AL-1972). */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-v11-content-bg px-4 py-6 md:px-8 md:py-8">
          <div className="w-full">{children}</div>
        </main>
      </div>
    </div>
  );
};

interface V11LayoutProps {
  children?: React.ReactNode;
}

export const V11Layout: React.FC<V11LayoutProps> = ({ children }) => (
  <V11LayoutProvider>
    <V11LayoutInner>{children}</V11LayoutInner>
  </V11LayoutProvider>
);
